import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  provideClientHydration,
  withHttpTransferCacheOptions,
  withEventReplay,
} from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { provideHttpClient, withFetch } from '@angular/common/http';
import { StoreModule } from '@ngrx/store';
import {
  companyReducer,
  tenantReducer,
  subscriptionPayloadReducer,
  countryListReducer,
  stateListReducer,
  cityListReducer,
  SignUpPaylinkReducer,
  StatsigReducer,
} from './state/app.reducer';
import { EffectsModule } from '@ngrx/effects';
import { CompanyEffects } from './state/app.effect';
import { SplashScreenStateService } from './services/resolvers/splash-screen-state.service';
import { delay } from './shared/directives/common-functions';
import { DatePipe, registerLocaleData } from '@angular/common';
import { TagStatsigService } from './services/statsig/tag-statsig.service';
import { AlertErrorHandler } from 'errors';
import {
  provideRouter,
  withComponentInputBinding,
  withRouterConfig,
} from '@angular/router';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { routes } from './app.routes';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { environment } from '../environments/environment';
import localeAr from '@angular/common/locales/ar';
import { setupQueryParamsInterceptor } from './core/query-params.interceptor';
import { PrimeNgLocaleSyncService } from './services/prime-ng-locale-sync.service';
import { provideWidgets } from 'widgets';
import { provideLazyTranslateLoader } from './core/i18n/provide-lazy-translate';

export function appInitializerFactory(
  splashScreenStateService: SplashScreenStateService,
) {
  return () => delay(50, splashScreenStateService)();
}

registerLocaleData(localeAr);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideClientHydration(
      withEventReplay(),
      withHttpTransferCacheOptions({
        includePostRequests: true,
      }),
    ),
    provideHttpClient(withFetch()),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.dark-mode',
        },
      },
    }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
        urlUpdateStrategy: 'eager',
      }),
    ),
    provideAppInitializer(() => {
      setupQueryParamsInterceptor();
    }),
    provideAppInitializer(() => {
      if (typeof window !== 'undefined' && window.self !== window.top) {
        inject(SplashScreenStateService).hideSplashScreen();
      }
    }),
    importProvidersFrom(TranslateModule.forRoot()),
    provideAppInitializer(() => {
      inject(PrimeNgLocaleSyncService);
    }),
    provideLazyTranslateLoader(),
    importProvidersFrom(
      StoreModule.forRoot({
        company: companyReducer,
        tenants: tenantReducer,
        subscriptionpayload: subscriptionPayloadReducer,
        countries: countryListReducer,
        states: stateListReducer,
        cities: cityListReducer,
        signuppaylinksteps: SignUpPaylinkReducer,
        statsigReducer: StatsigReducer,
      }),
    ),
    importProvidersFrom(EffectsModule.forRoot([CompanyEffects])),
    importProvidersFrom(
      GoogleTagManagerModule.forRoot({
        id: environment.GTMKey,
      }),
    ),
    DatePipe,
    TagStatsigService,
    SplashScreenStateService,
    { provide: ErrorHandler, useClass: AlertErrorHandler },
    provideWidgets({
      b2cApiUrl: environment.B2C_BASE_API_URL,
      b2bApiUrl: environment.BASE_URL,
      recaptcha: {
        v3SiteKey: environment.RECAPTCHA_V3_SITE_KEY ?? '',
        v2SiteKey: environment.RECAPTCHA_V2_SITE_KEY ?? '',
      },
    }),
  ],
};
